import { memo, useMemo } from 'react';
import { IconName } from '@optimus-web/icons';
import { Icon } from '../Icon';
import { Typography } from '../typography';
import {
    AvatarBadgeElement,
    AvatarContainerElement,
    AvatarElement,
    AvatarImageElement,
    BadgeImageElement,
    BadgeTitleContainer,
    getTitleTextStyle,
    NotificationElement,
} from './style';
import { AvatarProps } from './types';

const MemoizedAvatarContent = memo(
    ({
        src,
        alt,
        srcSet,
        sizes,
        title,
        icon,
        size,
    }: Pick<AvatarProps, 'src' | 'alt' | 'srcSet' | 'sizes' | 'title' | 'icon' | 'size'>) => {
        if (src) {
            return <AvatarImageElement src={src} alt={alt || title} srcSet={srcSet} sizes={sizes} objectFit="cover" />;
        }

        if (icon) {
            return <Icon value={icon as IconName} size={size} />;
        }

        if (title) {
            return (
                <Typography textStyle={getTitleTextStyle(size)} as="div">
                    {title
                        .trim()
                        .split(/\s+/g)
                        .slice(0, 2)
                        .map((word) => word[0].toUpperCase())
                        .join('')}
                </Typography>
            );
        }

        return null;
    }
);

export const Avatar = memo((props: AvatarProps) => {
    const { alt, emblem, children, title, src, srcSet, sizes, notification, size = 'large', icon, ...other } = props;
    const isValidSize = size === 'medium' || size === 'large';

    const emblemContent = useMemo(() => {
        if (!emblem || !isValidSize) return null;

        return (
            <AvatarBadgeElement size={size} border={!emblem.src} alignment="center" itemAlignment="center">
                {emblem.src && <BadgeImageElement {...emblem} />}
                {!emblem.src && emblem.title && (
                    <BadgeTitleContainer>
                        <Typography textStyle="bodySmallStrong" as="div">
                            {emblem.title[0]}
                        </Typography>
                    </BadgeTitleContainer>
                )}
            </AvatarBadgeElement>
        );
    }, [emblem, isValidSize, size]);

    const notificationElement = useMemo(() => {
        if (!notification || !isValidSize) return null;
        return <NotificationElement outline size={size} />;
    }, [isValidSize, notification, size]);

    return (
        <AvatarContainerElement {...other} alignment="center" itemAlignment="center">
            <AvatarElement size={size} border={!src}>
                <MemoizedAvatarContent
                    src={src}
                    alt={alt}
                    srcSet={srcSet}
                    sizes={sizes}
                    title={title}
                    icon={icon}
                    size={size}
                />
            </AvatarElement>
            {notificationElement}
            {emblemContent}
            {children}
        </AvatarContainerElement>
    );
});
